import { useCallback, useState } from "react";
import FilterDropdown from "../GlobalSearch/components/FilterDropdown";
import OrgSelectPopover from "../../common/OrgSelectPopover";
import { AccessibleOrganization } from "../../../store/slices/OrganizationsV3Slice";
import { UserListFilters } from "../../../services/UsersV3Service";
import { USER_ROLE_FILTER_OPTIONS, USER_STATUS_FILTER_OPTIONS } from "../../../constants/users";

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

interface UserFiltersProps {
  filters: UserListFilters;
  onChange: (filters: UserListFilters) => void;
  organizationOptions: AccessibleOrganization[];
  isOrganizationOptionsLoading: boolean;
  isOrganizationOptionsError: boolean;
}

// Role / Status / Organization dropdowns above the global user table. Role and
// Status are multi-select (empty = All); Organization is a single org and its
// sub-orgs, picked from the searchable hierarchy.
const UserFilters = ({
  filters,
  onChange,
  organizationOptions,
  isOrganizationOptionsLoading,
  isOrganizationOptionsError,
}: UserFiltersProps) => {
  const [openDropdown, setOpenDropdown] = useState<"role" | "status" | null>(null);

  const toggleRoleOpen = useCallback(
    () => setOpenDropdown((prev) => (prev === "role" ? null : "role")),
    []
  );
  const toggleStatusOpen = useCallback(
    () => setOpenDropdown((prev) => (prev === "status" ? null : "status")),
    []
  );

  return (
    <div className="flex flex-wrap items-center gap-3">
      <FilterDropdown
        label="Role"
        options={USER_ROLE_FILTER_OPTIONS}
        maxHeightClass="max-h-96"
        selectedValues={filters.role}
        onToggle={(value) => onChange({ ...filters, role: toggleValue(filters.role, value) })}
        onClearAll={() => onChange({ ...filters, role: [] })}
        isOpen={openDropdown === "role"}
        onToggleOpen={toggleRoleOpen}
      />
      <FilterDropdown
        label="Status"
        options={USER_STATUS_FILTER_OPTIONS}
        maxHeightClass="max-h-80"
        selectedValues={filters.status}
        onToggle={(value) => onChange({ ...filters, status: toggleValue(filters.status, value) })}
        onClearAll={() => onChange({ ...filters, status: [] })}
        isOpen={openDropdown === "status"}
        onToggleOpen={toggleStatusOpen}
      />
      <OrgSelectPopover
        options={organizationOptions}
        value={filters.organizationId}
        onChange={(organizationId) => onChange({ ...filters, organizationId })}
        allLabel="All (Parent)"
        isLoading={isOrganizationOptionsLoading}
        isError={isOrganizationOptionsError}
        onOpen={() => setOpenDropdown(null)}
      />
    </div>
  );
};

export default UserFilters;
